import { type AvailableLocale } from '@omnea-digital/toolbox'
import { type BreakdownConfig, type SourceConfig } from '@omnea-app/insights'
import { decodeCustomBreakdown, encodeCustomBreakdown, isCustomBreakdownKey } from '@omnea-app/insights/customBreakdown'
import { type ResolvedDateBucket } from '@omnea-app/insights/dateBucketing'
import { formatDateByGranularity } from '@omnea-app/insights/dateFormatters'
import { bucketLabelBounds, type BucketOverflowSide } from '@omnea-app/insights/numericBucketing'
import { bucketSentinelSide } from '@omnea-app/insights/segments'
import {
  isNumericBreakdownKey,
  type ResolvedBucketedDimension,
  type ResolvedCustomBreakdown,
} from '@omnea-app/insights/widgetQuery'
import { type AvailableBreakdown } from '../hooks/useAvailableBreakdowns'
import { formatNumber } from './formatNumber'

export { customBreakdownQuestion, resolveBreakdownLabel } from '@omnea-app/insights/customBreakdownLabels'

export const supportsCustomBreakdowns = (sourceConfig: SourceConfig | null | undefined): boolean =>
  sourceConfig?.formAnswers != null

/**
 * Whether the source's members expose path levels. A levelled key resolves against nothing without
 * them, so multi-level questions must not be offered for a source that lacks them.
 */
export const supportsPathLevels = (sourceConfig: SourceConfig | null | undefined): boolean =>
  (sourceConfig?.formAnswers?.pathLevels?.length ?? 0) > 0

/**
 * A bucketed numeric breakdown groups records into ranges the same way a second one would, so a
 * widget takes one; a numeric option is refused while a sibling row holds a numeric key of either kind.
 */
export const isSecondNumericBreakdown = (
  optionKey: string,
  otherRowKeys: string[],
  breakdowns: Record<string, BreakdownConfig> | undefined
): boolean => isNumericBreakdownKey(optionKey, breakdowns) && otherRowKeys.some((key) => isNumericBreakdownKey(key, breakdowns))

/** How many custom filters a source can carry at once — one per independent join path. */
export const customFilterCapacity = (sourceConfig: SourceConfig | null | undefined): number =>
  sourceConfig?.formAnswerFilterSlots?.length ?? 0

/** How many custom breakdowns a source can carry at once — the base path plus one per breakdown slot. */
export const customBreakdownCapacity = (sourceConfig: SourceConfig | null | undefined): number =>
  sourceConfig?.formAnswers == null ? 0 : 1 + (sourceConfig.formAnswerBreakdownSlots?.length ?? 0)

/** Every custom filter claims its own slot; collapsing onto a breakdown is decided at resolve time. */
export const countCustomFilters = (filterKeys: string[]): number => filterKeys.filter(isCustomBreakdownKey).length

type LevelledQuestion = Pick<AvailableBreakdown, 'questionName' | 'questionType' | 'sourceId' | 'pathLevels'>

type QuestionIdentity = { questionName: string; questionType: string; sourceId: string | null }

/** Levels of `question` already held by `keys` — a repeated level would be a dropped duplicate dimension. */
const levelsHeldBy = (question: QuestionIdentity, keys: string[]): Set<number> => {
  const held = new Set<number>()
  for (const key of keys) {
    const ref = decodeCustomBreakdown(key)
    if (
      ref?.level != null &&
      ref.questionName === question.questionName &&
      ref.questionType === question.questionType &&
      ref.sourceId === question.sourceId
    ) {
      held.add(ref.level)
    }
  }
  return held
}

/**
 * The key a picked question stores — a multi-level question defaults to its deepest level not held
 * by a sibling row, so repeating the question breaks down a second level rather than duplicating one.
 */
export const defaultBreakdownKey = (breakdown: LevelledQuestion, otherRowKeys: string[] = []): string => {
  const depth = breakdown.pathLevels?.length ?? 0
  const held = levelsHeldBy(breakdown, otherRowKeys)
  let level = depth
  while (level > 1 && held.has(level)) level -= 1
  return encodeCustomBreakdown({
    questionName: breakdown.questionName,
    questionType: breakdown.questionType,
    sourceId: breakdown.sourceId,
    ...(depth > 0 ? { level } : {}),
  })
}

/** The key without its level — what identifies the question in a picker. */
export const stripBreakdownLevel = (key: string): string => {
  const ref = decodeCustomBreakdown(key)
  if (ref?.level == null) return key
  return encodeCustomBreakdown({ questionName: ref.questionName, questionType: ref.questionType, sourceId: ref.sourceId })
}

/** A stored key's level state against the offered questions; null when the key has no levels to pick. */
export const levelSelectionFor = (
  key: string,
  options: LevelledQuestion[] | undefined,
  otherRowKeys: string[] = []
): { pathLevels: string[]; level: number | null; disabledLevels: Set<number>; keyAtLevel: (level: number) => string } | null => {
  const ref = decodeCustomBreakdown(key)
  if (!ref) return null
  const option = options?.find(
    (o) => o.questionName === ref.questionName && o.questionType === ref.questionType && o.sourceId === ref.sourceId
  )
  if (!option?.pathLevels?.length) return null
  return {
    pathLevels: option.pathLevels,
    level: ref.level ?? null,
    disabledLevels: levelsHeldBy(ref, otherRowKeys),
    keyAtLevel: (level) => encodeCustomBreakdown({ ...ref, level }),
  }
}

/** Question names are org-authored, so the event carries the type instead of an unbounded value. */
export const breakdownAnalyticsKey = (key: string): string =>
  isCustomBreakdownKey(key) ? `custom:${decodeCustomBreakdown(key)?.questionType ?? 'unknown'}` : key

export type BucketLabelResolver = (rawValue: string | null, dimensionIndex: number) => string | null

/**
 * The span one bucket covers, clipped to whichever ends the range bounds. Cube groups by the bucket
 * index, so the edges are multiplied back here.
 */
const numericBucketLabel = (bucket: ResolvedBucketedDimension, index: number, locale: AvailableLocale): string => {
  const { lo, hi } = bucketLabelBounds(index, bucket.w, bucket.min, bucket.max)
  const low = formatNumber(lo, bucket.unit, locale)
  return lo === hi ? low : `${low}–${formatNumber(hi, bucket.unit, locale)}`
}

type LabelTranslate = (key: string, values?: Record<string, string | number>) => string

const CUBE_TIME_VALUE = /^\d{4}-\d{2}-\d{2}T/

const overflowBucketLabel = (
  bucket: ResolvedBucketedDimension,
  side: BucketOverflowSide,
  t: LabelTranslate,
  locale: AvailableLocale
): string | null => {
  const bound = side === 'under' ? bucket.min : bucket.max
  if (bound == null) return null
  const value = formatNumber(bound, bucket.unit, locale)
  return side === 'under'
    ? t('widget.bucketing.label-underflow', { min: value })
    : t('widget.bucketing.label-overflow', { max: value })
}

/**
 * Renders custom breakdowns' raw Cube values as bucket labels; undefined when the query has no
 * dimension that needs one. Returns null for a value or dimension it doesn't own, so callers keep
 * their existing fallback. Option labels are keyed by dimension index, because two id-keyed
 * questions can answer under the same option id string.
 */
export const bucketLabelResolver = ({
  customBreakdowns,
  bucketedDimension,
  dateBuckets = [],
  t,
  optionLabels,
  locale,
}: {
  customBreakdowns: ResolvedCustomBreakdown[]
  bucketedDimension?: ResolvedBucketedDimension | null
  dateBuckets?: ResolvedDateBucket[]
  t: LabelTranslate
  optionLabels?: ReadonlyMap<number, ReadonlyMap<string, string>>
  locale: AvailableLocale
}): BucketLabelResolver | undefined => {
  const labelled = customBreakdowns.filter((c) => c.breakdown.valueMember !== 'value')
  if (labelled.length === 0 && bucketedDimension == null && dateBuckets.length === 0) return undefined
  const memberByIndex = new Map(labelled.map((c) => [c.dimensionIndex, c.breakdown.valueMember]))
  const dateGranularityByIndex = new Map(dateBuckets.map((bucket) => [bucket.dimensionIndex, bucket.granularity]))
  return (rawValue, dimensionIndex) => {
    if (rawValue == null) return null
    const dateGranularity = dateGranularityByIndex.get(dimensionIndex)
    if (dateGranularity != null) {
      // Superseded rows can hold another dimension's value at this position, and date-fns throws formatting it.
      return CUBE_TIME_VALUE.test(rawValue) ? formatDateByGranularity(rawValue, dateGranularity, t) : null
    }
    if (bucketedDimension != null && dimensionIndex === bucketedDimension.dimensionIndex) {
      const side = bucketSentinelSide(rawValue)
      if (side != null) return overflowBucketLabel(bucketedDimension, side, t, locale)
      // Rows are labelled by dimension position, and the rows on screen can be a superseded query's:
      // a breakdown added, removed or reordered puts a value that is not a bucket index at this
      // position, and bucketLabelBounds throws on one rather than labelling it.
      const index = Number(rawValue)
      return Number.isInteger(index) ? numericBucketLabel(bucketedDimension, index, locale) : null
    }
    const valueMember = memberByIndex.get(dimensionIndex)
    if (valueMember == null) return null
    if (valueMember === 'valueBool') {
      return t(rawValue === 'true' ? 'widget.chart.boolean-true' : 'widget.chart.boolean-false')
    }
    return optionLabels?.get(dimensionIndex)?.get(rawValue) ?? null
  }
}
